import React from "react";
import * as Icon from "react-feather";
import { Landmark, ShieldCheck } from "lucide-react";
import { toast as alert } from "react-toastify";

import "./classify-transaction-modal.css";

import BookkeepingService from "../../../../services/BookkeepingService";
import UtilityService from "../../../../services/UtilityService";

import WComponent from "../../../common/WComponent";
import Button from "../../../common/button/Button";
import ToolsModal from "../kit/ToolsModal";
import ToolsSelect from "../kit/ToolsSelect";
import ToolsSummaryCard from "../kit/ToolsSummaryCard";
import ToolsCallout from "../kit/ToolsCallout";

export const CUSTOM_RATE_OPTION_VALUE = "__custom__";
export const COMMON_TAX_NAMES = ["GST", "PST", "HST", "QST", "GST & PST"];

class ClassifyTransactionModal extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			customRateOpen: false,
			newTaxRateName: "",
			newTaxRatePercent: "",
		};
	}

	onTaxSelect = async selectedValue => {
		const { transaction, onTaxChange } = this.props;

		if (selectedValue === CUSTOM_RATE_OPTION_VALUE) {
			await this.update({ customRateOpen: true, newTaxRateName: "", newTaxRatePercent: "" });
			return;
		}
		await onTaxChange(transaction, selectedValue);
	};

	onApplyCustomRate = async () => {
		const { clientId, customTaxRates, taxOptions, transaction, onCustomTaxRatesChange, onTaxChange } = this.props;
		const { newTaxRateName, newTaxRatePercent } = this.state;

		const typedName = newTaxRateName.trim();
		const rateName = typedName || "Custom";
		const built = BookkeepingService.buildCustomTaxRateOption({ name: rateName, ratePercent: newTaxRatePercent });
		if (built.error) {
			alert.error(built.error);
			return;
		}

		const alreadyExists = taxOptions.includes(built.option);
		if (!alreadyExists) {
			const updatedRates = [...customTaxRates, built.option];
			BookkeepingService.saveCustomTaxRates({ clientId, customTaxRates: updatedRates });
			onCustomTaxRatesChange(updatedRates);
		}

		await this.update({ customRateOpen: false, newTaxRateName: "", newTaxRatePercent: "" });
		await onTaxChange(transaction, built.option);
		alert.success(`Applied "${built.option}".`);
	};

	onCancelCustomRate = () => {
		this.update({ customRateOpen: false, newTaxRateName: "", newTaxRatePercent: "" });
	};

	onClassificationSelect = async selectedName => {
		const { classifications, transaction, onClassificationChange } = this.props;
		if (!selectedName) return;

		const classificationObject = classifications.find(classification => classification.name === selectedName) || { name: selectedName };
		await onClassificationChange(transaction, classificationObject);
	};

	renderCustomRateRow = () => {
		const { newTaxRateName, newTaxRatePercent } = this.state;

		return (
			<div className="bk-classify__custom-rate-row">
				<input
					className="bk-classify__rate-name-input"
					list="bk-classify-tax-name-options"
					placeholder="Tax name (optional)…"
					value={newTaxRateName}
					onChange={e => this.update({ newTaxRateName: e.target.value })}
				/>
				<datalist id="bk-classify-tax-name-options">
					{COMMON_TAX_NAMES.map(name => (
						<option key={name} value={name} />
					))}
				</datalist>
				<input
					className="bk-classify__rate-percent-input"
					type="number"
					step="0.01"
					min="0"
					max="100"
					placeholder="%"
					value={newTaxRatePercent}
					onChange={e => this.update({ newTaxRatePercent: e.target.value })}
					autoFocus
				/>
				<Button
					text="Apply"
					view="secondary"
					onClick={this.onApplyCustomRate}
					disabled={newTaxRatePercent === ""}
					icon={<Icon.Check size={14} />}
				/>
				<Button text="Cancel" view="secondary" onClick={this.onCancelCustomRate} />
			</div>
		);
	};

	renderSummary = () => {
		const { transaction } = this.props;

		const amountIsNegative = Number(transaction.amount) < 0;
		const typeLabel = amountIsNegative ? "Withdrawal" : "Deposit";
		const formattedAmount = UtilityService.formatCurrency(transaction.amount);
		const amountWithoutSymbol = formattedAmount.replace("$", "");
		const currencyCode = transaction.currency || "CAD";
		const bankAccountName = transaction.bankAccountName || transaction.accountName || "";
		const tone = amountIsNegative ? "danger" : "success";

		const title = (
			<>
				<strong>{typeLabel}</strong> · {transaction.description}
			</>
		);

		const dateMeta = { icon: <Icon.Calendar size={13} />, text: transaction.date };
		const accountMeta = bankAccountName ? [{ icon: <Landmark size={13} />, text: bankAccountName }] : [];
		const meta = [dateMeta, ...accountMeta];

		return (
			<ToolsSummaryCard
				icon={<Landmark size={20} />}
				tone={tone}
				title={title}
				value={amountWithoutSymbol}
				valueSuffix={currencyCode}
				meta={meta}
			/>
		);
	};

	renderFooter = () => {
		const { onClose } = this.props;

		return (
			<>
				<span className="bk-classify__autosave">
					<ShieldCheck size={16} className="bk-classify__autosave-icon" />
					Changes save automatically
				</span>
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" onClick={onClose}>Cancel</button>
					<button type="button" className="tk-button tk-button--primary" onClick={onClose}>
						<Icon.Check size={16} />
						Done
					</button>
				</span>
			</>
		);
	};

	render() {
		const { transaction, classifications, taxOptions, hasTax, onMarkAsTransfer } = this.props;
		const { customRateOpen } = this.state;

		const transactionTax = transaction.tax || "";
		const transactionTaxIsMissingFromOptions = transactionTax !== "" && !taxOptions.includes(transactionTax);
		const hasClassification = transaction.classification && transaction.classification !== "Unclassified";
		const currentClassification = hasClassification ? transaction.classification : "";

		const classificationChoices = classifications.map(classification => ({
			value: classification.name,
			label: classification.name,
			hint: BookkeepingService.whichStatementAnAccountSitsOn({ statementType: classification.statement_type }),
		}));

		const missingTaxChoice = transactionTaxIsMissingFromOptions ? [{ value: transactionTax, label: transactionTax }] : [];
		const taxChoices = taxOptions.map(tax => ({ value: tax, label: tax }));
		const taxSelectChoices = [{ value: "", label: "No tax" }, ...missingTaxChoice, ...taxChoices, { value: CUSTOM_RATE_OPTION_VALUE, label: "+ Custom rate…" }];

		return (
			<ToolsModal open={true} onClose={this.props.onClose} title="Classify transaction" width={560} context={this.renderSummary()} footer={this.renderFooter()}>
				<div className="tk-form-field">
					<div className="tk-form-label">
						Classification
						<span className="tk-form-required">*</span>
					</div>
					<ToolsSelect
						value={currentClassification}
						options={classificationChoices}
						onChange={this.onClassificationSelect}
						placeholder="Select an account or category…"
						searchable
						searchPlaceholder={`Search ${classifications.length} accounts…`}
					/>
					<div className="tk-form-help">Choose where this money should be recorded.</div>
				</div>

				{hasTax && (
					<div className="tk-form-field">
						<div className="tk-form-label">
							Tax treatment
							<span className="tk-form-required">*</span>
						</div>
						<ToolsSelect
							value={transactionTax}
							options={taxSelectChoices}
							onChange={this.onTaxSelect}
							placeholder="No tax"
							disabled={!hasClassification}
						/>
						<div className="tk-form-help">
							{hasClassification
								? "This determines how tax will be applied."
								: "Choose a classification first — the treatment follows the account."}
						</div>
						{customRateOpen && this.renderCustomRateRow()}
					</div>
				)}

				{onMarkAsTransfer && (
					<div className="bk-classify__transfer-slot">
						<ToolsCallout
							icon={<Icon.Repeat size={16} />}
							tone="accent"
							title="Not a classification?"
							description="This might be a transfer between accounts."
							actionLabel="Mark as transfer"
							onClick={() => onMarkAsTransfer(transaction)}
						/>
					</div>
				)}
			</ToolsModal>
		);
	}
}

export default ClassifyTransactionModal;
